import React, { useMemo, useState } from 'react';
import { 
  Box, 
  Typography, 
  CircularProgress,
  Button,
  Divider,
  TextField,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  FormGroup,
} from '@mui/material';
import { useGetProductsByCategoryTreeQuery } from '../../../store/api/productsApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';
import PrintIcon from '@mui/icons-material/Print';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';

export default function ProductCatalogPrint() {
  const navigate = useNavigate();
  const { data: categoryTree, isLoading: loadingCategories } = useGetProductsByCategoryTreeQuery();
  const { data: company, isLoading: loadingCompany } = useGetCurrentCompanyQuery();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMainCategories, setSelectedMainCategories] = useState([]);
  const [selectedSubcategories, setSelectedSubcategories] = useState([]);
  const [selectedProductIds, setSelectedProductIds] = useState(new Set());

  const currency = company?.currency || 'USD';
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
  const baseUrl = apiUrl.replace('/api', '');

  // Flattened active products
  const allProducts = useMemo(() => {
    if (!categoryTree) return [];
    let list = [];
    categoryTree.forEach((mainCat) => {
      // General products
      const mainProds = (mainCat.products || []).filter(p => p.isActive).map(p => ({
        ...p,
        mainCatId: mainCat.id,
        mainCatName: mainCat.name,
        subCatId: 'general',
        subCatName: 'General'
      }));
      list.push(...mainProds);

      // Subcategory products
      mainCat.subcategories?.forEach((subCat) => {
        const subProds = (subCat.products || []).filter(p => p.isActive).map(p => ({
          ...p,
          mainCatId: mainCat.id,
          mainCatName: mainCat.name,
          subCatId: subCat.id,
          subCatName: subCat.name
        }));
        list.push(...subProds);
      });
    });
    return list;
  }, [categoryTree]);

  // Sidebar tree data
  const sidebarTree = useMemo(() => {
    if (!categoryTree) return [];
    return categoryTree.map((mainCat) => {
      const subcats = mainCat.subcategories?.map(s => ({
        id: s.id,
        name: s.name,
        count: (s.products || []).filter(p => p.isActive).length
      })) || [];
      
      const generalCount = (mainCat.products || []).filter(p => p.isActive).length;
      if (generalCount > 0) {
        subcats.unshift({ id: `general_${mainCat.id}`, name: 'General', count: generalCount });
      }

      const totalCount = subcats.reduce((acc, curr) => acc + curr.count, 0);

      return {
        id: mainCat.id,
        name: mainCat.name,
        count: totalCount,
        subcats
      };
    }).filter(m => m.count > 0);
  }, [categoryTree]);

  // Filter products for the *UI Grid*
  const filteredProducts = useMemo(() => {
    let list = allProducts;

    // Filter by selected main categories
    if (selectedMainCategories.length > 0) {
      list = list.filter(p => selectedMainCategories.includes(p.mainCatId));
    }

    // Filter by selected subcategories (if any main cat is selected, we might want to narrow down)
    // Actually, simple logic: if a main cat is checked, we show all its products unless specific subcats are checked.
    if (selectedSubcategories.length > 0) {
      list = list.filter(p => selectedSubcategories.includes(p.subCatId) || selectedSubcategories.includes(`general_${p.mainCatId}`));
    }

    // Filter by search
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(p => 
        (p.name && p.name.toLowerCase().includes(q)) ||
        (p.sku && p.sku.toLowerCase().includes(q)) ||
        (p.barcode && p.barcode.toLowerCase().includes(q)) ||
        (p.brand && p.brand.toLowerCase().includes(q))
      );
    }

    return list;
  }, [allProducts, selectedMainCategories, selectedSubcategories, searchQuery]);

  // Calculate printable structure based on ONLY the products that are checked!
  const printData = useMemo(() => {
    if (!categoryTree || selectedProductIds.size === 0) return [];
    const result = [];
    
    categoryTree.forEach((mainCat) => {
      const mainCatInfo = {
        name: mainCat.name,
        subcats: [],
      };
      
      // General
      const selectedMainProds = (mainCat.products || []).filter(p => selectedProductIds.has(p.id));
      if (selectedMainProds.length > 0) {
        mainCatInfo.subcats.push({ name: "General", products: selectedMainProds });
      }
      
      // Subcategories
      mainCat.subcategories?.forEach((subCat) => {
        const selectedSubProds = (subCat.products || []).filter(p => selectedProductIds.has(p.id));
        if (selectedSubProds.length > 0) {
          mainCatInfo.subcats.push({ name: subCat.name, products: selectedSubProds });
        }
      });
      
      if (mainCatInfo.subcats.length > 0) {
        result.push(mainCatInfo);
      }
    });
    
    return result;
  }, [categoryTree, selectedProductIds]);

  // Handlers
  const handleToggleProduct = (id) => {
    const newSet = new Set(selectedProductIds);
    if (newSet.has(id)) newSet.delete(id);
    else newSet.add(id);
    setSelectedProductIds(newSet);
  };

  const handleSelectAllVisible = () => {
    const newSet = new Set(selectedProductIds);
    filteredProducts.forEach(p => newSet.add(p.id));
    setSelectedProductIds(newSet);
  };

  const handleDeselectAllVisible = () => {
    const newSet = new Set(selectedProductIds);
    filteredProducts.forEach(p => newSet.delete(p.id));
    setSelectedProductIds(newSet);
  };

  const handleToggleMainCategory = (id) => {
    if (selectedMainCategories.includes(id)) {
      setSelectedMainCategories(prev => prev.filter(i => i !== id));
      // Also uncheck its subcategories
      const mainCat = sidebarTree.find(m => m.id === id);
      if (mainCat && mainCat.subcats) {
        const subCatIds = mainCat.subcats.map(s => s.id);
        setSelectedSubcategories(prev => prev.filter(i => !subCatIds.includes(i)));
      }
    } else {
      setSelectedMainCategories(prev => [...prev, id]);
    }
  };

  const handleToggleSubcategory = (id) => {
    if (selectedSubcategories.includes(id)) {
      setSelectedSubcategories(prev => prev.filter(i => i !== id));
    } else {
      setSelectedSubcategories(prev => [...prev, id]);
    }
  };

  if (loadingCategories || loadingCompany) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress size={60} sx={{ mb: 2, color: '#4ECDC4' }} />
        <Typography variant="h6" color="text.secondary">Loading Catalog...</Typography>
      </Box>
    );
  }

  // Define visual styles isolated to screen media
  const screenOnly = { '@media print': { display: 'none !important' } };
  const printOnly = { display: 'none', '@media print': { display: 'block !important' } };

  return (
    <Box 
      sx={{ 
        bgcolor: '#ffffff',
        minHeight: '100vh',
        '@media print': {
          bgcolor: '#ffffff',
          WebkitPrintColorAdjust: 'exact',
          printColorAdjust: 'exact',
        }
      }}
    >
      {/* ----------------- SCREEN VIEW (CATALOG BUILDER) ----------------- */}
      <Box sx={{ ...screenOnly, display: 'flex', flexDirection: 'column', height: '100vh' }}>
        {/* Top Controls Bar */}
        <Box 
          sx={{ 
            p: 2, 
            bgcolor: '#f8f9fa', 
            borderBottom: '1px solid #ddd',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
            zIndex: 10
          }}
        >
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ color: '#2C3E50' }}>
            Back to Products
          </Button>
          
          <Typography variant="h6" fontWeight="bold" sx={{ color: '#2C3E50' }}>
            Catalog Builder
          </Typography>
          
          <Button 
            startIcon={<PrintIcon />} 
            variant="contained" 
            onClick={() => window.print()}
            disabled={selectedProductIds.size === 0}
            sx={{ bgcolor: '#4ECDC4', '&:hover': { bgcolor: '#3BA89F' }, px: 3 }}
          >
            Print Selected ({selectedProductIds.size})
          </Button>
        </Box>

        <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
          {/* Left Sidebar Filters */}
          <Box sx={{ width: 280, borderRight: '1px solid #ddd', p: 2, bgcolor: '#fafafa', overflowY: 'auto' }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2, color: '#2C3E50' }}>
              Filter by Category
            </Typography>
            {sidebarTree.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No categories found.</Typography>
            ) : (
              <FormGroup>
                {sidebarTree.map(mainCat => (
                  <Box key={mainCat.id} sx={{ mb: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox 
                          checked={selectedMainCategories.includes(mainCat.id)}
                          onChange={() => handleToggleMainCategory(mainCat.id)}
                          size="small"
                          sx={{ color: '#4ECDC4', '&.Mui-checked': { color: '#4ECDC4' } }}
                        />
                      }
                      label={<Typography variant="body2" fontWeight="bold">{mainCat.name} ({mainCat.count})</Typography>}
                    />
                    {mainCat.subcats.map(subCat => (
                      <FormControlLabel
                        key={subCat.id}
                        sx={{ ml: 3, display: 'block' }}
                        control={
                          <Checkbox 
                            checked={selectedSubcategories.includes(subCat.id)}
                            onChange={() => handleToggleSubcategory(subCat.id)}
                            size="small"
                            sx={{ color: '#bdc3c7', '&.Mui-checked': { color: '#4ECDC4' } }}
                          />
                        }
                        label={<Typography variant="caption">{subCat.name} ({subCat.count})</Typography>}
                      />
                    ))}
                  </Box>
                ))}
              </FormGroup>
            )}
          </Box>

          {/* Right Main Area */}
          <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', bgcolor: '#f5f7fa', overflow: 'hidden' }}>
            {/* Search and Selection Tools */}
            <Box sx={{ p: 2, bgcolor: '#fff', borderBottom: '1px solid #eee', display: 'flex', gap: 2, alignItems: 'center' }}>
              <TextField 
                size="small"
                placeholder="Search matching products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ width: 300, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: '#bdc3c7' }} />
                    </InputAdornment>
                  ),
                }}
              />
              <Box sx={{ flexGrow: 1 }} />
              <Button size="small" variant="outlined" onClick={handleSelectAllVisible} sx={{ color: '#2C3E50', borderColor: '#bdc3c7' }}>
                Select All Visible
              </Button>
              <Button size="small" variant="outlined" color="error" onClick={handleDeselectAllVisible}>
                Deselect All Visible
              </Button>
            </Box>

            {/* Product Grid */}
            <Box sx={{ p: 3, overflowY: 'auto' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Showing {filteredProducts.length} product(s). Check the box to include them in your printed catalog.
              </Typography>

              {filteredProducts.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 10 }}>
                  <Typography color="text.secondary">No products match your filters.</Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 2 }}>
                  {filteredProducts.map(product => {
                    const isSelected = selectedProductIds.has(product.id);
                    return (
                      <Box 
                        key={product.id} 
                        onClick={() => handleToggleProduct(product.id)}
                        sx={{ 
                          bgcolor: '#fff', 
                          border: `2px solid ${isSelected ? '#4ECDC4' : 'transparent'}`,
                          borderRadius: 2, 
                          p: 1.5,
                          cursor: 'pointer',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                          position: 'relative',
                          transition: 'all 0.2s',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                          }
                        }}
                      >
                        <Checkbox 
                          checked={isSelected}
                          sx={{ 
                            position: 'absolute', 
                            top: 4, 
                            right: 4, 
                            color: '#bdc3c7',
                            '&.Mui-checked': { color: '#4ECDC4' }
                          }}
                        />
                        <Box sx={{ height: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f9f9f9', borderRadius: 1, mb: 1, overflow: 'hidden' }}>
                          {product.imageUrl ? (
                            <img 
                              src={`${baseUrl}${product.imageUrl}`} 
                              alt="img"
                              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          ) : (
                            <Typography variant="caption" color="text.secondary">No Image</Typography>
                          )}
                        </Box>
                        <Typography variant="caption" sx={{ color: '#7f8c8d', display: 'block' }}>{product.sku || product.barcode}</Typography>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.2, mb: 0.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                          {product.name}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#4ECDC4' }}>
                          {currency} {Number(product.sellingPrice || 0).toFixed(2)}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ----------------- PRINT LAYOUT (ONLY VISIBLE ON PRINT) ----------------- */}
      <Box sx={{ ...printOnly, p: { xs: 2, md: 4 }, maxWidth: 1000, margin: '0 auto' }}>
        {selectedProductIds.size === 0 ? (
          <Typography variant="h5" align="center" sx={{ mt: 10 }}>No products selected for printing.</Typography>
        ) : (
          <>
            {/* Cover / Header Section */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4, pb: 2, borderBottom: '2px solid #2C3E50' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {company?.logoPath ? (
                  <img 
                    src={`${baseUrl}${company.logoPath}`} 
                    alt="Company Logo" 
                    style={{ height: 60, objectFit: 'contain' }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ) : null}
                <Box>
                  <Typography variant="h4" fontWeight="bold" color="#2C3E50">{company?.name || 'Product Catalog'}</Typography>
                  {(company?.phone || company?.email) && (
                    <Typography variant="body2" color="text.secondary">
                      {company?.phone && `Tel: ${company.phone}`} {company?.phone && company?.email && '| '} {company?.email && `Email: ${company.email}`}
                    </Typography>
                  )}
                </Box>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="h5" color="#4ECDC4" fontWeight="900" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>Product Catalog</Typography>
                <Typography variant="body2" color="text.secondary">Date: {new Date().toLocaleDateString()}</Typography>
              </Box>
            </Box>

            {/* Categories Loop */}
            {printData.map((mainCat, mainIdx) => (
              <Box key={`print-main-${mainIdx}`} sx={{ mb: 6, pageBreakInside: 'avoid' }}>
                {/* Main Category Header */}
                <Typography 
                  variant="h5" 
                  fontWeight="bold" 
                  sx={{ 
                    bgcolor: '#2C3E50', 
                    color: 'white', 
                    p: 1.5, 
                    mb: 3,
                    borderRadius: 1,
                    pageBreakAfter: 'avoid'
                  }}
                >
                  {mainCat.name}
                </Typography>

                {/* Subcategories Loop */}
                {mainCat.subcats.map((subcat, subIdx) => (
                  <Box key={`print-sub-${subIdx}`} sx={{ mb: 4, ml: 2 }}>
                    <Typography 
                      variant="h6" 
                      fontWeight="600" 
                      color="#2C3E50" 
                      sx={{ mb: 2, borderBottom: '1px solid #eee', pb: 1, pageBreakAfter: 'avoid' }}
                    >
                      {subcat.name}
                    </Typography>

                    {/* Products Grid */}
                    <Box 
                      sx={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(4, 1fr)', 
                        gap: 3 
                      }}
                    >
                      {subcat.products.map(product => (
                        <Box 
                          key={`print-prod-${product.id}`} 
                          sx={{ 
                            border: '1px solid #e0e0e0', 
                            borderRadius: 2,
                            p: 1.5,
                            display: 'flex',
                            flexDirection: 'column',
                            pageBreakInside: 'avoid'
                          }}
                        >
                          {/* Product Image */}
                          <Box sx={{ width: '100%', height: 120, mb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f9f9f9', borderRadius: 1 }}>
                            {product.imageUrl ? (
                              <img 
                                src={`${baseUrl}${product.imageUrl}`} 
                                alt={product.name} 
                                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            ) : (
                              <Typography variant="caption" color="text.secondary">No Image</Typography>
                            )}
                          </Box>
                          
                          {/* Product Details */}
                          <Typography variant="caption" sx={{ color: '#7f8c8d', textTransform: 'uppercase', fontSize: '0.65rem' }}>
                            {product.sku || product.barcode || 'N/A'}
                          </Typography>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', lineHeight: 1.2, mb: 0.5, flexGrow: 1 }}>
                            {product.name}
                          </Typography>
                          {product.brand && (
                            <Typography variant="caption" sx={{ color: '#95a5a6', mb: 0.5 }}>
                              Brand: {product.brand}
                            </Typography>
                          )}
                          
                          <Divider sx={{ my: 1 }} />
                          
                          <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#4ECDC4' }}>
                            {currency} {Number(product.sellingPrice || 0).toFixed(2)}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ))}
              </Box>
            ))}

            {/* Footer */}
            <Box sx={{ mt: 5, pt: 2, borderTop: '1px solid #ddd', textAlign: 'center', position: 'fixed', bottom: 20, width: '100%', left: 0 }}>
              <Typography variant="caption" color="text.secondary">
                {company?.name} • Selected Products Catalog
              </Typography>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}
